import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import AudioRecorder from './AudioRecorder';
import { translateZoneName } from '@medical/shared/utils/translateZoneName';
import { useMedicationReminders } from '../contexts/MedicationReminderContext';

// Visual Analog Scale (VAS): 0 = No pain, 10 = Worst pain imaginable (standard clinical scale)
const VAS_MIN = 0;
const VAS_MAX = 10;

// Face + descriptor reference (matches common VAS/Faces scale): 0, 1-3, 4-6, 7-9, 10
export const VAS_FACE_RANGES = [
  { value: 0, range: '0', emoji: '😊', descriptorKey: 'noPain', color: '#10b981' },
  { value: 2, range: '1-3', emoji: '🙂', descriptorKey: 'mild', color: '#60a5fa' },
  { value: 5, range: '4-6', emoji: '😐', descriptorKey: 'moderate', color: '#f59e0b' },
  { value: 8, range: '7-9', emoji: '😢', descriptorKey: 'verySevere', color: '#f97316' },
  { value: 10, range: '10', emoji: '😭', descriptorKey: 'worstPain', color: '#ef4444' }
];

// Get face/descriptor for current VAS value
export function getVASFaceDescriptor(vasValue) {
  if (vasValue == null || vasValue === 0) return VAS_FACE_RANGES[0];
  if (vasValue <= 3) return VAS_FACE_RANGES[1];
  if (vasValue <= 6) return VAS_FACE_RANGES[2];
  if (vasValue <= 9) return VAS_FACE_RANGES[3];
  return VAS_FACE_RANGES[4];
}

// Derive pain level category and color from VAS score — 5 categories (VAS-aligned)
export function vasToPainLevel(vasScore) {
  if (vasScore == null || vasScore === 0) return { painLevel: 'no-pain', painColor: '#10b981' };
  if (vasScore <= 3) return { painLevel: 'mild', painColor: '#60a5fa' };
  if (vasScore <= 6) return { painLevel: 'moderate', painColor: '#f59e0b' };
  if (vasScore <= 9) return { painLevel: 'severe', painColor: '#f97316' };
  return { painLevel: 'worst-pain', painColor: '#ef4444' };
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 0.5rem;
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
`;

const ZoneName = styled.p`
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
`;

const VASSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
`;

const VASLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #374151;
`;

const VASSlider = styled.input`
  width: 100%;
  height: 12px;
  -webkit-appearance: none;
  appearance: none;
  background: linear-gradient(to right, #10b981 0%, #60a5fa 30%, #f59e0b 70%, #ef4444 100%);
  border-radius: 6px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${props => props.$thumbColor || '#3b82f6'};
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }

  &::-moz-range-thumb {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${props => props.$thumbColor || '#3b82f6'};
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }
`;

const VASEndLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
`;

const VASCurrentDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: ${props => props.$bg || '#f0fdf4'};
  border: 2px solid ${props => props.$border || '#10b981'};
  border-radius: 0.75rem;
  font-weight: 600;
  color: ${props => props.$color || '#166534'};
`;

const VASCurrentEmoji = styled.span`
  font-size: 2rem;
  line-height: 1;
`;

const VASFacesRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 1rem;
  gap: 0.25rem;
`;

const VASFaceButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem;
  border: 2px solid ${props => props.$selected ? (props.$color || '#3b82f6') : '#e5e7eb'};
  border-radius: 0.75rem;
  background: ${props => props.$selected ? (props.$color ? props.$color + '18' : '#eff6ff') : '#fff'};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;

  &:hover {
    transform: translateY(-2px);
    border-color: ${props => props.$color || '#94a3b8'};
    background: ${props => props.$color ? props.$color + '22' : '#f1f5f9'};
  }
`;

const VASFaceEmoji = styled.span`
  font-size: 1.5rem;
  line-height: 1.2;
  margin-bottom: 0.25rem;
`;

const VASFaceRange = styled.span`
  font-size: 0.65rem;
  color: #6b7280;
  font-weight: 600;
`;

const VASNumbersRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #374151;
  padding: 0 4px;
  & > span {
    flex: 1;
    text-align: center;
    min-width: 0;
  }
`;

const VASPlusMinusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const VASStepButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  background: #fff;
  color: #374151;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
  user-select: none;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #3b82f6;
    color: #3b82f6;
  }
  &:active:not(:disabled) {
    transform: scale(0.96);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const VASValueBadge = styled.div`
  min-width: 3.5rem;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${props => props.$color || '#111827'};
  text-align: center;
`;

const AdditionalInfoSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SymptomsCheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f9fafb;
`;

const SymptomCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background 0.15s ease;
  user-select: none;

  &:hover {
    background: #f3f4f6;
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    min-width: 18px;
    cursor: pointer;
    accent-color: #3b82f6;
    flex-shrink: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;

  &.primary {
    background-color: #3b82f6;
    color: white;

    &:hover:not(:disabled) {
      background-color: #2563eb;
    }
  }

  &.secondary {
    background-color: #6b7280;
    color: white;

    &:hover:not(:disabled) {
      background-color: #4b5563;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PainScaleModal = ({ zoneName, onSelect, onClose, view }) => {
  const { t } = useTranslation();
  const { pendingPostMedication, clearPendingPostMedication } = useMedicationReminders();
  const [vasValue, setVasValue] = useState(0);
  const [notes, setNotes] = useState('');
  const [symptoms, setSymptoms] = useState([]);
  const [triggers, setTriggers] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('');
  const [mood, setMood] = useState('');
  const [activity, setActivity] = useState('');
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordAsPostMedication, setRecordAsPostMedication] = useState(!!pendingPostMedication);
  React.useEffect(() => {
    setRecordAsPostMedication(!!pendingPostMedication);
  }, [pendingPostMedication]);

  const { painLevel, painColor } = vasToPainLevel(vasValue);
  const currentFace = getVASFaceDescriptor(vasValue);
  const descriptorLabel = t(`painScale.vasDescriptors.${currentFace.descriptorKey}`, { defaultValue: currentFace.descriptorKey });

  const handleSymptomToggle = (symptomKey) => {
    setSymptoms(prev => {
      const current = Array.isArray(prev) ? prev : [];
      if (symptomKey === 'none') {
        return current.includes('none') ? [] : ['none'];
      }
      let updated = current.filter(s => s !== 'none');
      if (updated.includes(symptomKey)) {
        updated = updated.filter(s => s !== symptomKey);
      } else {
        updated = [...updated, symptomKey];
      }
      return updated;
    });
  };

  const handleSubmit = () => {
    const base = vasValue === 0
      ? {
          painIntensity: 0,
          notes: '',
          symptoms: [],
          triggers: [],
          timeOfDay: undefined,
          mood: undefined,
          activity: undefined,
          audioBlob: null,
          view: view || 'front'
        }
      : {
          painIntensity: vasValue,
          notes: notes.trim(),
          symptoms: symptoms.filter(s => s !== 'none'),
          triggers: triggers.split(',').map(txt => txt.trim()).filter(Boolean),
          timeOfDay: timeOfDay || undefined,
          mood: mood || undefined,
          activity: activity || undefined,
          audioBlob: audioBlob,
          view: view || 'front'
        };
    if (recordAsPostMedication && pendingPostMedication?.medicationName && pendingPostMedication?.takenAt) {
      base.recordedAfterMedication = true;
      base.medicationTakenAt = pendingPostMedication.takenAt;
      base.postMedicationMedicationName = pendingPostMedication.medicationName;
      clearPendingPostMedication();
    }
    onSelect(painLevel, painColor, base);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t('painScale.vasTitle', { defaultValue: 'Visual Analog Scale (VAS)' })}</ModalTitle>
          <ZoneName>{translateZoneName(zoneName, t)}</ZoneName>
          {view && (
            <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
              {view === 'front' ? t('painScale.frontView') : t('painScale.backView')}
            </div>
          )}
          <div style={{
            fontSize: '0.8rem',
            color: '#6b7280',
            marginTop: '0.75rem',
            padding: '0.5rem',
            backgroundColor: '#f0f9ff',
            borderRadius: '0.5rem',
            border: '1px solid #bae6fd'
          }}>
            {t('painScale.vasDescription', { defaultValue: 'Standard 0–10 scale: 0 = No pain, 10 = Worst pain imaginable' })}
          </div>
        </ModalHeader>

        <VASSection>
          <VASLabelRow>
            <span>{t('painScale.vasCurrentValue', { defaultValue: 'Your pain level' })}</span>
          </VASLabelRow>

          <VASPlusMinusRow>
            <VASStepButton
              type="button"
              onClick={() => setVasValue((v) => Math.max(VAS_MIN, v - 1))}
              disabled={vasValue <= VAS_MIN}
              title={t('painScale.vasDecrease', { defaultValue: 'Decrease' })}
              aria-label={t('painScale.vasDecrease', { defaultValue: 'Decrease value' })}
            >
              −
            </VASStepButton>
            <VASValueBadge $color={painColor}>{vasValue}</VASValueBadge>
            <VASStepButton
              type="button"
              onClick={() => setVasValue((v) => Math.min(VAS_MAX, v + 1))}
              disabled={vasValue >= VAS_MAX}
              title={t('painScale.vasIncrease', { defaultValue: 'Increase' })}
              aria-label={t('painScale.vasIncrease', { defaultValue: 'Increase value' })}
            >
              +
            </VASStepButton>
          </VASPlusMinusRow>

          <VASNumbersRow>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <span key={n}>{n}</span>
            ))}
          </VASNumbersRow>
          <VASSlider
            type="range"
            min={VAS_MIN}
            max={VAS_MAX}
            value={vasValue}
            onChange={(e) => setVasValue(Number(e.target.value))}
            $thumbColor={painColor}
          />
          <VASEndLabels>
            <span>{t('painScale.vasDescriptors.noPain', { defaultValue: 'No Pain' })}</span>
            <span>{t('painScale.vasDescriptors.worstPain', { defaultValue: 'Worst Pain Possible' })}</span>
          </VASEndLabels>

          <VASCurrentDisplay $bg={currentFace.color + '15'} $border={currentFace.color} $color={currentFace.color}>
            <VASCurrentEmoji>{currentFace.emoji}</VASCurrentEmoji>
            <span>{descriptorLabel}</span>
            <span> (VAS {vasValue})</span>
          </VASCurrentDisplay>

          <VASFacesRow>
            {VAS_FACE_RANGES.map((item) => {
              const matches = (item.value === 0 && vasValue === 0) || (item.value === 2 && vasValue >= 1 && vasValue <= 3) || (item.value === 5 && vasValue >= 4 && vasValue <= 6) || (item.value === 8 && vasValue >= 7 && vasValue <= 9) || (item.value === 10 && vasValue === 10);
              return (
                <VASFaceButton
                  key={item.range}
                  type="button"
                  $selected={matches}
                  $color={item.color}
                  onClick={() => setVasValue(item.value)}
                  title={t(`painScale.vasDescriptors.${item.descriptorKey}`, { defaultValue: item.descriptorKey })}
                >
                  <VASFaceEmoji>{item.emoji}</VASFaceEmoji>
                  <VASFaceRange>{item.range}</VASFaceRange>
                </VASFaceButton>
              );
            })}
          </VASFacesRow>
        </VASSection>

        {pendingPostMedication?.medicationName && (
          <FormGroup style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0f9ff', borderRadius: '0.5rem', border: '1px solid #bae6fd' }}>
            <SymptomCheckboxLabel htmlFor="recordAsPostMedication">
              <input
                id="recordAsPostMedication"
                type="checkbox"
                checked={recordAsPostMedication}
                onChange={(e) => setRecordAsPostMedication(e.target.checked)}
              />
              <span>
                {t('painScale.recordAfterMedication', {
                  defaultValue: 'This is my pain ~30 min after taking "{{medication}}" (for doctor to see effect)',
                  medication: pendingPostMedication.medicationName
                })}
              </span>
            </SymptomCheckboxLabel>
          </FormGroup>
        )}

        {vasValue > 0 && (
          <AdditionalInfoSection>
            <SectionTitle>{t('painScale.additionalInfo')}</SectionTitle>

            <FormGroup>
              <Label>{t('painScale.painScale')}</Label>
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#f3f4f6',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: painColor
              }}>
                VAS {vasValue} – {descriptorLabel}
              </div>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="timeOfDay">{t('painScale.timeOfDay')}</Label>
              <Select id="timeOfDay" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)}>
              <option value="">{t('painScale.placeholders.selectTimeOfDay')}</option>
                <option value="morning">{t('painScale.timeOptions.morning')}</option>
                <option value="afternoon">{t('painScale.timeOptions.afternoon')}</option>
                <option value="evening">{t('painScale.timeOptions.evening')}</option>
                <option value="night">{t('painScale.timeOptions.night')}</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="mood">{t('painScale.mood')}</Label>
              <Select id="mood" value={mood} onChange={(e) => setMood(e.target.value)}>
                <option value="">{t('painScale.placeholders.selectMood', { defaultValue: 'Select mood (optional)' })}</option>
                <option value="good">{t('painScale.moodOptions.good')}</option>
                <option value="moderate">{t('painScale.moodOptions.moderate')}</option>
                <option value="bad">{t('painScale.moodOptions.bad')}</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="activity">{t('painScale.activity')}</Label>
              <Select id="activity" value={activity} onChange={(e) => setActivity(e.target.value)}>
                <option value="">{t('painScale.placeholders.selectActivity', { defaultValue: 'Select activity (optional)' })}</option>
                <option value="able">{t('painScale.activityOptions.able')}</option>
                <option value="partiallyDependent">{t('painScale.activityOptions.partiallyDependent')}</option>
                <option value="notAble">{t('painScale.activityOptions.notAble')}</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>{t('painScale.symptoms')}</Label>
              <SymptomsCheckboxGroup>
                {['none', 'vomiting', 'nausea', 'dizziness', 'headache', 'fever', 'fatigue', 'shortnessOfBreath', 'cough', 'swelling', 'numbness', 'weakness'].map((symptomKey) => {
                  const isChecked = Array.isArray(symptoms) && symptoms.includes(symptomKey);
                  return (
                    <SymptomCheckboxLabel key={symptomKey} htmlFor={`symptom-${symptomKey}`}>
                      <input
                        id={`symptom-${symptomKey}`}
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleSymptomToggle(symptomKey)}
                      />
                      <span>{t(`painScale.symptomOptions.${symptomKey}`)}</span>
                    </SymptomCheckboxLabel>
                  );
                })}
              </SymptomsCheckboxGroup>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="triggers">{t('painScale.triggers')}</Label>
              <Input
                id="triggers"
                type="text"
                value={triggers}
                onChange={(e) => setTriggers(e.target.value)}
                placeholder={t('painScale.placeholders.triggers')}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="notes">{t('painScale.notes')}</Label>
              <TextArea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('painScale.placeholders.notes')}
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('painScale.audioNote')}</Label>
              <AudioRecorder onAudioRecorded={setAudioBlob} existingAudio={audioBlob} />
            </FormGroup>
          </AdditionalInfoSection>
        )}

        <ButtonGroup>
          <Button type="button" className="secondary" onClick={handleCancel}>
            {t('painScale.buttons.cancel')}
          </Button>
          <Button
            type="button"
            className="secondary"
            onClick={() => { onSelect(null, null, { clear: true }); onClose(); }}
          >
            {t('painScale.buttons.clear')}
          </Button>
          <Button type="button" className="primary" onClick={handleSubmit}>
            {t('painScale.buttons.record')}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PainScaleModal;
